
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import Component from 'vue-class-component';
import moment, {MomentInput} from 'moment';
import { TableItemFormatter } from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import { getTableName, TIMEZONE_OFFSET_MS } from '@/worker/db/sqlHelpers';

@Component({
  components: {
    TableFilterable,
  },
})
export default class TiarcoChemicalMOtherIncomeBreakdownTable extends FilterWidget {
  public filterIds: Array<
      | 'date'
      | 'month'
      | 'dateAsOf'
      | 'dateRange'
      | 'stockItems'
      | 'agents'
      | 'customers'
      | 'suppliers'
      | 'project'
  > = ['suppliers', 'agents', 'dateAsOf', 'month', 'project'];

  public tableItems: object[] = [];
  public tableFields: object[] = [];
  public accountingDate: string = '';

    public get currentYearRange(): [number, number] {
        const dateFormat = 'DD/MM/YYYY';

        const accountingMonth = moment(this.accountingDate, dateFormat).month();
        const selectedDate = moment(this.selectedAsOfDate);
        const selectedYear = selectedDate.year();
        const selectedMonth = selectedDate.month();

        const startYear = selectedMonth < accountingMonth ? selectedYear - 1 : selectedYear;

        const startDate = moment()
            .year(startYear)
            .month(accountingMonth)
            .startOf('month')
            .valueOf();

        const endDate = moment(this.selectedAsOfDate).endOf('month').valueOf();
        return [startDate, endDate];
    }

  public getMonthYearArray(startDate, endDate) {
    const start = moment(startDate);
    const end = moment(endDate);

    const monthYearArray: string[] = [];

    while (start.isBefore(end) || start.isSame(end, 'month')) {
      monthYearArray.push(start.format('MMMYY'));
      start.add(1, 'month');
    }

    return monthYearArray;
  }

  public exportName(range: number[]) {
    return 'Other Income Breakdown' + ' from ' +
        moment(range[0] as MomentInput).format('DD MMM YY') +
        ' - ' +
        moment(range[1] as MomentInput).format('DD MMM YY');
  }

  public get expensiveHook() {
    const {
      selectedDateRange,
      selectedAsOfDate,
      currentYearRange,
        selectedProjects,
    } = this;
    return JSON.stringify([
      selectedDateRange,
      selectedAsOfDate,
      currentYearRange,
        selectedProjects,
    ]);
  }

  public async expensiveCalc() {
    const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
    const table = getTableName('transactions');
    const systemTable = getTableName('system');
    const amt = 'TRY_CAST("AMOUNT" AS DOUBLE)';
    const dateExpr = 'TRY_CAST("DOCDATE" AS BIGINT)';
    const yearExpr = `EXTRACT(YEAR FROM EPOCH_MS(`
        + `${dateExpr} + ${TIMEZONE_OFFSET_MS}))`;
    const monthExpr = `EXTRACT(MONTH FROM EPOCH_MS(`
        + `${dateExpr} + ${TIMEZONE_OFFSET_MS}))`;

    let projectFilter = '';
    if (this.selectedProjects.length > 0) {
      const list = this.selectedProjects
          .map((p) => `'${escapeStr(p)}'`).join(',');
      projectFilter = ` AND "PROJECT" IN (${list})`;
    }

    const jobId = this.currentJobId;
    const queryHead = await this._ensureTablesLoaded(
        ['transactions', 'system'],
    );
    this.checkInterrupted(jobId);

    // Load accounting date
    const systemResult = await queryHead.rawQuery(
        `SELECT "VAL" FROM ${systemTable} LIMIT 1`,
    );
    this.accountingDate = systemResult[0] && systemResult[0].VAL || '';

    const range = this.currentYearRange;

    this.generateLoadingText(0.3);

    // Cross-tab query: SPECIALACCTYPENAME × year/month
    const crossResult = await queryHead.rawQuery(`
        SELECT "SPECIALACCTYPENAME" as name,
            ${yearExpr} as year,
            ${monthExpr} as month,
            SUM(${amt}) as total
        FROM ${table}
        WHERE ${dateExpr}
            BETWEEN ${range[0]} AND ${range[1]}
            AND "ACCTYPE" = 'OI'
            ${projectFilter}
        GROUP BY name, year, month
    `);
    this.checkInterrupted(jobId);

    // Build month labels from date range
    const allMonthYears = this.getMonthYearArray(range[0], range[1]);

    // Build lookup: name → MMMYY → total
    const nameSet = new Set<string>();
    const crossTab: Record<string, number> = {};
    for (const row of crossResult) {
      const name = String(row.name);
      if (!name) { continue; }
      nameSet.add(name);
      const m = moment().year(Number(row.year))
          .month(Number(row.month) - 1);
      const key = `${name}-${m.format('MMMYY')}`;
      crossTab[key] = (crossTab[key] || 0) + (Number(row.total) || 0);
    }

    // Build table items
    const groupData: any[] = [];
    nameSet.forEach((name) => {
      const item: any = { particular: name };
      allMonthYears.forEach((my) => {
        item[my] = (crossTab[`${name}-${my}`] || 0) * -1;
      });
      groupData.push(item);
    });

    // Build table fields
    this.tableFields = [
      { key: 'particular', sortable: false },
      ...allMonthYears.map((my) => ({
        key: my,
        sortable: false,
        formatter: TableItemFormatter.currencySplit,
      })),
    ];

    this.tableItems = groupData;

    this.generateLoadingText(1);

    this.saveHistory('tableItems', 'tableFields');
  }


}
